import { useEffect, useState } from 'react';
import { createSelector } from 'reselect';

import {
  IAdvancedRole,
  isAdvancedRoleSlug,
  TAdvancedRoleSlug,
  advancedRoleStatus,
} from '@/features/roles/store/models/advancedRole.model';
import { TAppState } from '@/store/reducers/reducers';
import { RolesActionValues } from '@/features/roles/store/types/roles.types';
import { setAdvancedRole } from '@/features/roles/store/actions/advancedRole.actions';
import { RoleServices } from '@/services/RoleServices';
import { SupportUserService } from '@/features/dashboard-users/services/SupportUserService';
import { setCurrentSupportUser } from '@/features/dashboard-users/store/support-user.actions';

import { useAppDispatch, useAppSelector } from '@/hooks/redux-helpers';
import { decode_token } from '@/features/dashboard-users/helpers/decode_token';
import { getAdvandeRoleFromScopes } from '@/hooks/useAccessControl';

const selectUser = (state: TAppState) => state.auth?.currentSupportUser?.state;
const selectRole = (state: TAppState) => state.auth.currentSupportUser?.state?.metadata?.role;
const selectToken = (state: TAppState) => state.auth?.token?.access_token;
const selectAccessControlRole = (state: TAppState) => state.accessControl.role.value;
const selectRolesConfig = (state: TAppState) => state.accessControl.config.value;

export default function useAdvancedRole(): IAdvancedRole {
  const dispatch = useAppDispatch();
  const extractState = createSelector(
    [selectUser, selectToken, selectRole, selectAccessControlRole, selectRolesConfig],
    (currentUser, token, role, accessControlRole, config) => {
      return { currentUser, token, role, accessControlRole, config };
    },
  );

  const { token, currentUser, role, accessControlRole, config } = useAppSelector((state) => extractState(state));
  const jwtDecoded = decode_token(token);

  useEffect(() => {
    const updateRole = async () => {
      let supportUser = currentUser;

      const supportUserId = jwtDecoded.sub;

      if ((!currentUser && supportUserId) || supportUserId !== supportUser.id) {
        supportUser = await SupportUserService.searchSupportUser(supportUserId);
        dispatch(setCurrentSupportUser({ ...supportUser, metadata: supportUser.metadata || {} }));
      }

      let assumeRole;

      if (supportUser?.metadata?.role) {
        assumeRole = await RoleServices.GetRole(supportUser.metadata.role.slug as TAdvancedRoleSlug);
      } else {
        const scopes = jwtDecoded.scope;
        const rolesConfig = config || (await RoleServices.GetAllRoles(true));
        assumeRole = getAdvandeRoleFromScopes(scopes, rolesConfig);
        dispatch(setAdvancedRole(assumeRole.slug, advancedRoleStatus.PENDING, supportUserId));
      }

      dispatch({ type: RolesActionValues.GET_ROLE_SUCCESS, shouldSkipToaster: true, role: assumeRole });
    };

    if (token && (!role || !accessControlRole || accessControlRole.slug !== role.slug)) {
      updateRole();
    }
  }, [role, currentUser, token, dispatch, config, accessControlRole, jwtDecoded?.scope, jwtDecoded?.sub]);

  const defaultAssumeRole = getAdvandeRoleFromScopes(jwtDecoded?.scope, config);

  const slug = role?.slug;

  if (isAdvancedRoleSlug(slug)) {
    return { ...(role ? { ...role, slug } : defaultAssumeRole) };
  } else {
    return { ...defaultAssumeRole };
  }
}

export function useAdvancedRoleDetails(roleSlug: TAdvancedRoleSlug) {
  const [role, setRole] = useState<IAdvancedRole | undefined>(undefined);

  useEffect(() => {
    const searchRole = async () => {
      const role = await RoleServices.GetRole(roleSlug);
      setRole(role);
    };
    searchRole();
  }, [roleSlug]);

  return role;
}

export function useAllAdvancedRoles() {
  const [roles, setRoles] = useState<IAdvancedRole[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const searchRoles = async () => {
      const roles = await RoleServices.GetAllRoles();
      setRoles(roles);
      setIsLoading(false);
    };
    searchRoles();
  }, []);

  return { roles, isLoading };
}
